import { useThrottledCallback } from "@react-hookz/web";
import { useFlag } from "@unleash/nextjs";
import type {
  PlayerState,
  SpeechifyExperienceEvent,
  SpeechifyPlayerConfig,
} from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import { useAtomValue, useSetAtom } from "jotai";
import intersection from "lodash/intersection";
import type { RefObject } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { usePaywallCheckerContext } from "~/components/article/article-paywall-checker/contexts";
import { checkIsPostiesArticle } from "~/components/article/article-render/posties/helpers";
import { SpeechSkipClassName } from "~/components/article/article-speech/consts";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useArticleSpeechConfig } from "~/lib/app/hooks/scmp-article-speech-config";
import { onlyOnePlayingPlayerAtom } from "~/lib/only-one-playing-player";
import { rosettaAtom } from "~/lib/rosetta";
import { ZIndexLayer } from "~/lib/styles";
import type { hooksDetermineHasArticleSpeechUsageArticle$key } from "~/queries/__generated__/hooksDetermineHasArticleSpeechUsageArticle.graphql";

import { HidePlayerBySectionEntityIds, HidePlayerByTypeEntityIds } from "./consts";

import type { Variants } from "./index";

export const useBuildSpeechifyEventHandler = () => {
  const dispatch = useSetAtom(onlyOnePlayingPlayerAtom);

  const sendProgressTrackingEvent = useThrottledCallback(
    ({ label }: { label: string }) => {
      sendGATracking({
        action: "TTS/Progress",
        category: "TTS",
        label,
      });
    },
    [sendGATracking],
    1000,
    true,
  );

  const speechifyEventHandler = (
    event: SpeechifyExperienceEvent,
    playerState: PlayerState | undefined,
  ) => {
    switch (event.type) {
      case "pause":
        dispatch({
          type: "pauseArticleSpeechifyPlayer",
        });
        sendGATracking({
          action: "TTS/Pause",
          category: "TTS",
          label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
        });
        break;
      case "play":
        dispatch({
          type: "playArticleSpeechifyPlayer",
        });
        sendGATracking({
          action: "TTS/Play",
          category: "TTS",
          label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
        });
        break;
      case "progress":
        const percentageElapsed = Math.round((playerState?.progressFraction ?? 0) * 100);
        if (percentageElapsed > 0 && percentageElapsed % 10 === 0) {
          sendProgressTrackingEvent({
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
        }
        break;
      case "progress-finished":
        sendGATracking({
          action: "TTS/Progress Finished",
          category: "TTS",
          label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
        });
        break;
      case "resume":
        dispatch({
          type: "playArticleSpeechifyPlayer",
        });
        sendGATracking({
          action: "TTS/Resume",
          category: "TTS",
          label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
        });
        break;
    }
  };

  return {
    speechifyEventHandler,
  };
};

type UseBuildSpeechifyConfigParameters = {
  contentParentElement: HTMLElement | null;
  inlinePLayerId?: string;
  inlinePlayerReference: RefObject<HTMLDivElement | null>;
  variant: Variants;
};

export const useBuildSpeechifyConfig = ({
  contentParentElement,
  inlinePLayerId,
  inlinePlayerReference,
  variant,
}: UseBuildSpeechifyConfigParameters) => {
  const builtConfig = useMemo(() => {
    if (!contentParentElement) return null;

    const config = {
      customStyles: {
        playButton: {
          background: "radial-gradient(#ffffff 50%, transparent 50%)",
        },
        primaryColor: "#ff7c1d",
        widget: {
          borderRadius: "8px",
          zIndex: `${ZIndexLayer.Drawer - 1}`,
        },
      },
      ignoreElements(element) {
        return (
          element.tagName === "NOSCRIPT" ||
          element.tagName === "STYLE" ||
          element.tagName === "SCRIPT" ||
          element.classList.contains(SpeechSkipClassName)
        );
      },
      paragraphSettings: {
        isEnabled: "while-playing",
        marginRight: "-10px",
      },
      rootElement: contentParentElement,
      showFeedbackForm: false,
    } as Partial<SpeechifyPlayerConfig>;

    if (variant === "default") {
      config.inlinePlayerSelector = `#${inlinePLayerId}`;
      config.inlinePlayerElement = inlinePlayerReference.current ?? undefined;
    }

    return config;
  }, [contentParentElement, inlinePLayerId, inlinePlayerReference, variant]);

  return { config: builtConfig };
};

export const useDetermineHasArticleSpeechUsage = (
  reference: hooksDetermineHasArticleSpeechUsageArticle$key,
) => {
  const article = useFragment(
    graphql`
      fragment hooksDetermineHasArticleSpeechUsageArticle on Article {
        ...helpersCheckIsPostiesArticle
        ...hooksPaywallPromiseContent
        entityId
        sections {
          value {
            entityId
          }
        }
        types {
          value {
            entityId
          }
        }
        topics {
          entityId
        }
      }
    `,
    reference,
  );

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isPostiesArticle = checkIsPostiesArticle(article);

  const isShowForPostiesArticle = useMemo(() => {
    const shouldShowPaywall =
      asyncRosettaState?.status === "success" && !asyncRosettaState.result?.hasPostiesAccessRight;

    return isPostiesArticle && !shouldShowPaywall;
  }, [
    asyncRosettaState?.result?.hasPostiesAccessRight,
    asyncRosettaState?.status,
    isPostiesArticle,
  ]);

  const isSCMPArticleSpeechEnabled = useFlag("enable_scmp_article_speech");

  const sanitizedSectionEntityIds = useMemo(
    () =>
      article.sections?.flatMap(
        section => section?.value?.map(element => element?.entityId ?? "") ?? [],
      ) ?? [],
    [article.sections],
  );

  const sanitizedTypeEntityIds = useMemo(
    () =>
      article.types?.flatMap(type => type?.value?.map(element => element?.entityId ?? "") ?? []) ??
      [],
    [article.types],
  );

  const sanitizedTopicEntityIds = useMemo(
    () => article.topics?.map(topic => topic?.entityId) ?? [],
    [article.topics],
  );

  const isHideSCMPArticleSpeechRequired = useMemo(() => {
    if (!isSCMPArticleSpeechEnabled) return true;

    const doesArticleHaveHideSectionEntityId =
      intersection(HidePlayerBySectionEntityIds, sanitizedSectionEntityIds).length > 0;

    const doesArticleHaveHideTypeEntityId =
      intersection(HidePlayerByTypeEntityIds, sanitizedTypeEntityIds).length > 0;

    return doesArticleHaveHideSectionEntityId || doesArticleHaveHideTypeEntityId;
  }, [isSCMPArticleSpeechEnabled, sanitizedSectionEntityIds, sanitizedTypeEntityIds]);

  const { scmpArticleSpeechConfigState } = useArticleSpeechConfig();

  const isForceHideSCMPArticleSpeechWidget = useMemo(() => {
    if (!scmpArticleSpeechConfigState) return false;

    const hasArticleEntityId = (
      scmpArticleSpeechConfigState.forceHideByArticleEntityId ?? []
    ).includes(article.entityId);

    const hasSectionEntityId =
      intersection(
        scmpArticleSpeechConfigState.forceHideBySectionEntityId ?? [],
        sanitizedSectionEntityIds,
      ).length > 0;

    const hasTypeEntityId =
      intersection(
        scmpArticleSpeechConfigState.forceHideByTypeEntityId ?? [],
        sanitizedTypeEntityIds,
      ).length > 0;

    const hasTopicEntityId =
      intersection(
        scmpArticleSpeechConfigState.forceHideByTopicEntityId ?? [],
        sanitizedTopicEntityIds,
      ).length > 0;

    return hasArticleEntityId || hasSectionEntityId || hasTypeEntityId || hasTopicEntityId;
  }, [
    article.entityId,
    sanitizedSectionEntityIds,
    sanitizedTopicEntityIds,
    sanitizedTypeEntityIds,
    scmpArticleSpeechConfigState,
  ]);

  const { paywallMap } = usePaywallCheckerContext();

  const isShowingSCMPArticlePaywall = useMemo(() => {
    if (!paywallMap || Object.keys(paywallMap).length === 0) return true;

    const hasCurrentArticleEntityId = Object.keys(paywallMap).includes(article.entityId);

    if (!hasCurrentArticleEntityId) return true;

    return hasCurrentArticleEntityId && paywallMap[article.entityId];
  }, [article.entityId, paywallMap]);

  return {
    showArticleSpeechWidget:
      isShowForPostiesArticle ||
      (!isHideSCMPArticleSpeechRequired &&
        !isForceHideSCMPArticleSpeechWidget &&
        !isShowingSCMPArticlePaywall),
  };
};
